import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Send, User, Wifi } from 'lucide-react';
import { useTeamMessages } from '@/hooks/useTeamMessages';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/hooks/useTranslation';

interface TeamMessagingProps {
  teamId: string;
  teamName: string;
}

export const TeamMessaging = ({ teamId, teamName }: TeamMessagingProps) => {
  const { t } = useTranslation();
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showMessaging, setShowMessaging] = useState(false);
  const [lastViewedMessageCount, setLastViewedMessageCount] = useState(0);
  const { messages, loading, sendMessage } = useTeamMessages(teamId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Track unread messages when chat is hidden
  useEffect(() => {
    if (!showMessaging && messages.length > lastViewedMessageCount) {
      setUnreadCount(messages.length - lastViewedMessageCount);
    } else if (showMessaging) {
      // Reset unread count when chat is visible
      setUnreadCount(0);
      setLastViewedMessageCount(messages.length);
    }
  }, [messages.length, showMessaging, lastViewedMessageCount]);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    const result = await sendMessage(teamId, newMessage);
    
    if (result) {
      setNewMessage('');
    }
    
    setIsSending(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleToggleMessaging = () => {
    setShowMessaging(!showMessaging);
    if (!showMessaging) {
      // When opening chat, reset unread count and update last viewed count
      setUnreadCount(0);
      setLastViewedMessageCount(messages.length);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getDisplayName = (message: any) => {
    return message.profiles?.farm_name || message.profiles?.username || 'Anonymous';
  };

  const getAvatarUrl = (message: any) => {
    return message.profiles?.avatar_url || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm">
            <MessageCircle className="h-4 w-4" />
            {t('teams.teamChat')} - {teamName}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={isOnline ? "default" : "destructive"} className="text-xs">
              <Wifi className="h-3 w-3 mr-1" />
              {isOnline ? t('teams.online') : t('teams.offline')}
            </Badge>
            {messages.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {messages.length} messages
              </Badge>
            )}
          </div>
        </div>
        <CardDescription>
          {t('teams.tagline')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toggle Button with Unread Indicator */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleToggleMessaging}
            className="flex-1 relative"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            {showMessaging ? t('teams.hideMessages') : t('teams.teamMessages')}
            {!showMessaging && unreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>
        </div>

        {showMessaging && (
          <>
            {/* Messages Display */}
            <div 
              ref={messagesContainerRef}
              className="max-h-96 overflow-y-auto space-y-3 border rounded-lg p-4 bg-gray-50"
            >
              {loading ? (
                <div className="text-center text-gray-500 py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                  <p>{t('messaging.loadingMessages')}</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t('messaging.noMessages')}</p>
                  <p className="text-sm">{t('messaging.firstMessage')}</p>
                </div>
              ) : (
                <>
                  {messages.map((message, index) => {
                    const showAvatar = index === 0 || messages[index - 1].sender_id !== message.sender_id;
                    const isLastFromSender = index === messages.length - 1 || messages[index + 1]?.sender_id !== message.sender_id;
                    
                    return (
                      <div key={message.id} className={`flex items-start gap-3 ${!showAvatar ? 'ml-12' : ''}`}>
                        {showAvatar && (
                          <div className="bg-green-100 rounded-full p-1 flex-shrink-0">
                            <img 
                              src={getAvatarUrl(message)}
                              alt={getDisplayName(message)}
                              className="h-8 w-8 rounded-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face";
                              }}
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          {showAvatar && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-green-800">
                                {getDisplayName(message)}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDateTime(message.created_at)}
                              </span>
                            </div>
                          )}
                          <div className={`bg-white rounded-lg p-3 border border-gray-200 ${isLastFromSender ? 'mb-2' : 'mb-1'}`}>
                            <p className="text-sm text-gray-900 break-words whitespace-pre-wrap">
                              {message.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <div className="space-y-3">
              <Textarea
                placeholder={t('teams.typeMessage')}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="min-h-[80px] resize-none"
                maxLength={500}
                disabled={!isOnline}
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  {newMessage.length}/500 {t('messaging.charactersUsed')}
                  {!isOnline && ` • ${t('messaging.offlineMode')}`}
                </span>
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isSending || !isOnline}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isSending ? t('teams.sending') : t('teams.sendMessage')}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
